import { motion } from "framer-motion";
import { Clock, TrendingUp } from "lucide-react";
import { SectionHead, Reveal } from "@/components/Reveal";

const BLOCK_LABELS = [
  ["warmup", "Warm-Up"],
  ["prep", "Movement Prep"],
  ["main", "Main Workout"],
  ["core", "Core Conditioning"],
  ["stretch", "Static Stretching"],
];

const PROGRAMS = [
  {
    num: "01", name: "Learn to Squat & Hinge", caps: ["Squat", "Bend & Lift"],
    duration: "35 min", level: "Beginner",
    why: "Own the two patterns behind sitting, standing and picking anything up.",
    blocks: {
      warmup: [["Brisk march on the spot", "3 min"]],
      prep: [["Squat-to-Stand", "2 × 6"], ["Hip Hinge Drill", "2 × 8"]],
      main: [["Box Squat", "3 × 8"], ["Glute Bridge", "3 × 12"]],
      core: [["Dead Bug", "3 × 8/side"]],
      stretch: [["Figure-4 Stretch", "60 s/side"]],
    },
  },
  {
    num: "02", name: "Push & Pull Basics", caps: ["Push", "Pull"],
    duration: "35 min", level: "Beginner",
    why: "Balanced upper-body strength for doors, trolleys, bags and shelves.",
    blocks: {
      warmup: [["Band Pull-Apart", "2 × 15"], ["Easy walk", "3 min"]],
      prep: [["Incline Push-Up", "2 × 6"]],
      main: [["Band Chest Press", "3 × 12"], ["Band Row", "3 × 12"]],
      core: [["Bird Dog", "3 × 8/side"]],
      stretch: [["Open Book", "6/side"]],
    },
  },
  {
    num: "03", name: "Carry Strong", caps: ["Pull", "Core"],
    duration: "30 min", level: "Beginner",
    why: "Grip and posture for groceries, luggage and everything heavy in life.",
    blocks: {
      warmup: [["Tall march in place", "3 min"]],
      prep: [["Suitcase Hold (light)", "2 × 20 s/side"]],
      main: [["Farmer Carry (light)", "4 × 20 m"], ["Step-Up (low box)", "3 × 8/side"]],
      core: [["Side Plank (knees)", "3 × 15 s/side"]],
      stretch: [["Child's Pose Side Reach", "60 s/side"]],
    },
  },
  {
    num: "04", name: "Everyday Endurance", caps: ["Endurance"],
    duration: "40 min", level: "Beginner",
    why: "Build the engine for stairs, long walks and busy days — gently.",
    blocks: {
      warmup: [["Easy walk-jog", "4 min"]],
      prep: [["Ladder Walk-Throughs", "3 × through"]],
      main: [["Walk / Easy Jog Intervals", "6 × 2 min"], ["Elevated Mountain Climbers", "3 × 20 s"]],
      core: [["Hollow Hold (tuck)", "3 × 15 s"]],
      stretch: [["Couch Stretch", "60 s/side"]],
    },
  },
];

export default function SamplePrograms() {
  return (
    <section id="programs" data-testid="programs-section" className="bg-zinc-950 border-y border-zinc-800/70 py-24 sm:py-32 px-5 sm:px-10 lg:px-16">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <SectionHead overline="Sample program sessions">
          Start here.
          <span className="block text-stroke">Built the capable way.</span>
        </SectionHead>
        <Reveal delay={0.12}>
          <p className="text-zinc-400 max-w-sm text-sm sm:text-base leading-relaxed">
            Beginner-friendly by design — every session follows the same proven structure:
            warm-up, movement prep, main workout, core conditioning, static stretching.
          </p>
        </Reveal>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {PROGRAMS.map((p, i) => (
          <Reveal key={p.num} delay={(i % 2) * 0.06} className="h-full">
            <motion.article
              data-testid={`program-card-${p.num}`}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="group h-full border border-zinc-800 bg-ink p-7 sm:p-8 flex flex-col hover:border-volt/60 transition-colors duration-500"
            >
              <div className="flex items-start justify-between">
                <span className="font-display text-lg tracking-widest text-zinc-600 group-hover:text-volt transition-colors">{p.num}</span>
                <span className="text-[9px] uppercase tracking-[0.18em] font-extrabold text-volt border border-volt/40 px-2.5 py-1">
                  {p.level}
                </span>
              </div>
              <h3 className="font-display uppercase text-3xl sm:text-4xl leading-[0.92] mt-4">{p.name}</h3>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[10px] uppercase tracking-[0.16em] font-bold text-zinc-500">
                <span className="flex items-center gap-1.5"><Clock size={12} className="text-volt" /> {p.duration}</span>
                <span className="flex items-center gap-1.5"><TrendingUp size={12} className="text-volt" /> {p.caps.join(" + ")}</span>
              </div>
              <p className="text-sm text-zinc-400 mt-4 leading-relaxed">{p.why}</p>
              <div className="mt-5 pt-5 border-t border-zinc-800 space-y-4 flex-1">
                {BLOCK_LABELS.map(([key, label]) => (
                  <div key={key}>
                    <p className="text-[9px] uppercase tracking-[0.22em] font-extrabold text-volt mb-1.5">{label}</p>
                    <ul className="space-y-1.5">
                      {p.blocks[key].map(([m, r]) => (
                        <li key={m} className="flex justify-between gap-3 text-sm">
                          <span className="text-zinc-200">{m}</span>
                          <span className="text-zinc-500 whitespace-nowrap">{r}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.article>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

